/**
 * AI CONTEXT: architecture - App composition, providers, and routing
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Compose global providers and error boundary
 * - Define SPA routes for all main pages
 * - Initialize AWS Amplify configuration
 * 
 * Future considerations:
 * - Code-split heavy routes
 * - Add protected route guard component
 * - Track route analytics
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { GiveawayProvider } from './contexts/GiveawayContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ProductProvider } from './contexts/ProductContext';
import ErrorBoundary from './components/ErrorBoundary';
import CodeProtection from './components/CodeProtection';
import { useAuth } from './contexts/AuthContext';

// Initialize AWS Amplify configuration
import './services/aws-config';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import MobileNav from './components/layout/MobileNav';
import ScrollToTop from './components/ScrollToTop';
import RecentSalesPopup from './components/RecentSalesPopup';
import SupportChatBubble from './components/SupportChatBubble';

// Pages
import Home from './pages/Home';
import Store from './pages/Store';
import ProductDetail from './pages/ProductDetail';
import Giveaways from './pages/Giveaways';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import ConfirmAccount from './pages/ConfirmAccount';
import Admin from './pages/Admin';
import Legal from './pages/Legal';
import Checkout from './pages/Checkout';
import NotificationSettings from './components/NotificationSettings';
import AgeVerification from './pages/AgeVerification';
import ReferralShare from './pages/ReferralShare';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Acquisition from './pages/Acquisition';

// Feature flags - easily toggle features on/off
const FEATURE_FLAGS = {
  RECENT_SALES_POPUP: false, // Client request: take down recent purchase popup
  POPUP_FREQUENCY: 'minimal', // Options: 'constant', 'reduced', 'minimal'
  // Public blog always on (client request: Blog tab must be visible)
  AI_BLOG: true,
};

function ProtectedRoute({ children, requireAuth = false, requireAdmin = false }) {
  const { authChecked, loading, isAuthenticated, isAdmin } = useAuth();
  if (!authChecked || loading) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-theme-silver">Loading…</div>
      </div>
    );
  }
  if (requireAdmin) {
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!isAdmin) return <Navigate to="/" replace />;
    return children;
  }
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <div className="min-h-screen bg-theme-black">
      <ScrollToTop />
      <Header />
      <main className="pt-16 pb-20 bg-theme-black">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/store" element={<Store />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/giveaways" element={<Giveaways />} />
          <Route path="/acquisition" element={<Acquisition />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/confirm-account" element={<ConfirmAccount />} />
          <Route path="/legal" element={<Legal />} />
          {FEATURE_FLAGS.AI_BLOG && (
            <>
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </>
          )}
          <Route path="/age-verification" element={<AgeVerification />} />
          <Route path="/checkout" element={<Checkout />} />
          {/* Shareable referral link route */}
          <Route path="/share/:referralCode" element={<ReferralShare />} />

          {/* Protected user routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireAuth>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute requireAuth>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />

          {/* Admin-only route */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />

          {/* Fallback to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <MobileNav />
      {/* Support Chat Bubble - Always visible for authenticated users */}
      <SupportChatBubble />
      {/* Recent Sales Popup - Frequency controlled via feature flag */}
      {FEATURE_FLAGS.RECENT_SALES_POPUP && <RecentSalesPopup frequency={FEATURE_FLAGS.POPUP_FREQUENCY} />}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { background: '#363636', color: '#fff' },
          success: { duration: 3000, iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { duration: 5000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <CartProvider>
            <SubscriptionProvider>
              <GiveawayProvider>
                <ProductProvider>
                  <CodeProtection protectionLevel="standard">
                    <AppRoutes />
                  </CodeProtection>
                </ProductProvider>
              </GiveawayProvider>
            </SubscriptionProvider>
          </CartProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App; 